import { ProductType, ProductEnablementStatus } from '@/generated/enums'
import { useGetActiveAndTrialOrganizationProductsQuery } from '@/generated/graphql'

/**
 * Hook that fetches and maps enabled products for an organization.
 * @param orgId The organization ID to fetch products for
 * @param isInternal Whether to return all products for internal users
 * @returns An object containing all enabled products, just the trial products optionally if isEmployeeView is enabled, and loading state
 */

export type OrgEnabledProducts = {
  products: ProductType[]
  trialProducts?: ProductType[]
  loading: boolean
  error: Error | undefined
}

export const useOrgEnabledProducts = (
  orgId: string | null,
  isEmployeeView?: boolean,
): OrgEnabledProducts => {
  const { data, loading, error } = useGetActiveAndTrialOrganizationProductsQuery({
    variables: { organization_id: orgId },
    skip: !orgId,
  })

  const products =
    data?.organization_products
      ?.map((p) => {
        const productStr = p.product
        const productEnum = Object.values(ProductType).find((val) => val === productStr)
        return productEnum || null
      })
      .filter((p): p is ProductType => p !== null) || []

  let trialProducts: ProductType[] = []
  if (isEmployeeView) {
    trialProducts =
      data?.organization_products
        ?.filter((p) => p.enablement_status === ProductEnablementStatus.TRIAL)
        ?.map((p) => {
          const productStr = p.product
          const productEnum = Object.values(ProductType).find(
            (val) => val === productStr,
          )
          return productEnum || null
        })
        .filter((p): p is ProductType => p !== null) || []
  }

  return {
    products,
    ...(isEmployeeView ? { trialProducts } : {}),
    loading,
    error,
  }
}
